import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Box, Button } from "@chakra-ui/react";
import { exportBusinessTransactions } from "api/business-user-management";
import { IExportBusinessTransactionsPayload } from "api/business-user-management/types";
import { ReactComponent as RefreshIcon } from "assets/icons/table-refresh-icon.svg";
import { useBusinesses } from "contexts/businesses-context";
import { useFilterQueryParams } from "hooks/useFilterQueryParams";
import { useUpdateQueryParams } from "hooks/useUpdateQueryParams";
import { debounce } from "lodash";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  capitalizeWords,
  formatDateToYYYYMMDD,
  handleDownloadBufferData,
  haveQueryParamsChanged,
} from "utils";
import {
  NO_EXPORT_DATA_FOUND,
  NO_NEW_FILTER_MESSAGE,
  START_INDEX,
  ZERO,
} from "utils/constants";
import { SEARCH_DEBOUNCE_TIMER } from "utils/constants";

import {
  customErrorToast,
  customInfoToast,
  customSuccessToast,
  customWarningToast,
} from "ui/custom-toast";
import { IDropDownRef } from "ui/dropdown/types";
import { Export } from "ui/export";
import { IExportDateRangeValues, TExportType } from "ui/export/types";
import { Filter } from "ui/filter";
import {
  IDateRangeValues,
  IFilterRef,
  TFilterOption,
  TFilterValue,
  TFilterValues,
} from "ui/filter/types";
import { tableHeaderCtaCss } from "ui/table/styles";
import { TableSearchInput } from "ui/table/table-search-input";

import { DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS } from "./const";
import { IBusinessTransactionsTableHeaderCTAsProps } from "./types";

const SOURCE_ACCOUNT_POSITION = 2;
const MAX_TRANSACTION_FILTER_OPTIONS = 4;
const DEFAULT_FILTER_OPTIONS: TFilterOption[] = [
  {
    type: "checkbox",
    title: "Status",
    key: "status",
    options: [
      {
        label: "All",
        value: "all",
      },
      {
        label: "Successful",
        value: "successful",
      },
      {
        label: "Pending",
        value: "pending",
      },
      {
        label: "Failed",
        value: "failed",
      },
    ],
  },
  {
    type: "checkbox",
    title: "Transaction Type",
    key: "transactionType",
    options: [
      {
        label: "All",
        value: "all",
      },
      {
        label: "Credit",
        value: "credit",
      },
      {
        label: "Debit",
        value: "debit",
      },
    ],
  },
  {
    type: "date-range",
    title: "Custom Date Range",
    key: "dateRange",
  },
];
export const ALL_BUSINESS_TRANSACTION_TYPE_LIST = ["all", "credit", "debit"];
export const ALL_BUSINESS_TRANSACTION_STATUS_LIST = [
  "all",
  "successful",
  "pending",
  "failed",
];

const AVAILABLE_EXPORT_OPTIONS: TExportType[] = ["csv", "pdf"];
export const BusinessTransactionsTableHeaderCTAs = memo(
  (props: IBusinessTransactionsTableHeaderCTAsProps) => {
    const {
      handleGetBusinessTransactions,
      isLoading,
      isRefreshing,
      setIsRefreshing,
    } = props;
    const { businessId } = useParams();
    const location = useLocation();
    const updateQueryParams = useUpdateQueryParams();
    const { isFilterError, setIsFilterError, filterRetryCountParams } =
      useFilterQueryParams({
        apiFilterRoute: "/control/business/compliance/business/transactions",
      });
    const [searchParams] = useSearchParams();
    const type = searchParams.get("type");
    const status = searchParams.get("status");
    const accountName = searchParams.get("accountName");
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");
    const search = searchParams.get("search");
    const [searchQuery, setSearchQuery] = useState(search || "");
    const filterRef = useRef<IFilterRef | null>(null);
    const { isLoadingBusinessAccounts, businessAccountsData } = useBusinesses();
    const [isFiltering, setIsFiltering] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [filterValues, setFilterValues] = useState<TFilterValues>({
      status: status ? status.split(",") : [],
      transactionType: type ? type.split(",") : [],
      dateRange: {
        startDate,
        endDate,
      },
      sourceAccount: accountName || "",
    });

    const isFilterDisabled = !(
      (filterValues?.transactionType as string[])?.length > ZERO ||
      (filterValues?.dateRange as IDateRangeValues)?.startDate ||
      filterValues?.sourceAccount ||
      (filterValues?.status as string[])?.length > ZERO
    );

    const handleResetFilter = () => {
      setFilterValues({
        status: [],
        transactionType: [],
        dateRange: {
          startDate: null,
          endDate: null,
        },
        sourceAccount: "",
      });
    };

    const onBusinessTransactionsSearch = useCallback(
      debounce(async (value: string) => {
        if (value.trim() === "") {
          updateQueryParams({
            ...Object.fromEntries(new URLSearchParams(location.search)),
            search: null,
            page: String(DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS.page),
            limit: String(
              DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS.limit
            ),
          });
          return;
        }

        updateQueryParams({
          ...Object.fromEntries(new URLSearchParams(location.search)),
          search: value,
          page: String(DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS.page),
          limit: String(DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS.limit),
        });
      }, SEARCH_DEBOUNCE_TIMER),
      [location]
    );

    const handleSearchQueryChange = (
      evt: React.ChangeEvent<HTMLInputElement>
    ) => {
      const { value } = evt.target;
      setSearchQuery(value);
      onBusinessTransactionsSearch(value);
    };

    const handleFilterValueChange = (key: string, value: TFilterValue) => {
      let newValue = value;
      if (key === "status") {
        const transactionStatus = value as string[];
        const currentTransactionStatus = filterValues?.status as string[];
        if (
          transactionStatus.includes("all") &&
          currentTransactionStatus.length <
            ALL_BUSINESS_TRANSACTION_STATUS_LIST.length
        ) {
          newValue = ALL_BUSINESS_TRANSACTION_STATUS_LIST;
        } else {
          newValue = transactionStatus.filter(
            (trxStatus) => trxStatus !== "all"
          );
        }
      } else if (key === "transactionType") {
        const transactionType = value as string[];
        const currentTransactionType =
          filterValues?.transactionType as string[];
        if (
          transactionType.includes("all") &&
          currentTransactionType.length <
            ALL_BUSINESS_TRANSACTION_TYPE_LIST.length
        ) {
          newValue = ALL_BUSINESS_TRANSACTION_TYPE_LIST;
        } else {
          newValue = transactionType.filter((trxType) => trxType !== "all");
        }
      }
      setFilterValues((prev) => ({
        ...prev,
        [key]: newValue,
      }));
    };

    const handleApplyFilter = async () => {
      const newQueryParams: Record<string, string | null> = {};
      const startDate = (filterValues.dateRange as IDateRangeValues).startDate;
      const endDate = (filterValues.dateRange as IDateRangeValues).endDate;
      const status = filterValues.status;
      const accountName = filterValues.sourceAccount as string;
      const type = filterValues.transactionType;

      if (startDate) {
        newQueryParams["startDate"] = startDate;
      } else {
        newQueryParams["startDate"] = null;
      }

      if (endDate) {
        newQueryParams["endDate"] = endDate;
      } else {
        newQueryParams["endDate"] = null;
      }

      if (accountName) {
        newQueryParams["accountName"] = accountName;
      } else {
        newQueryParams["accountName"] = null;
      }

      if (Array.isArray(status) && status.length > ZERO) {
        newQueryParams["status"] = status.join(",");
      } else {
        newQueryParams["status"] = null;
      }

      if (Array.isArray(type) && type.length > ZERO) {
        newQueryParams["type"] = type.join(",");
      } else {
        newQueryParams["type"] = null;
      }

      newQueryParams["page"] =
        DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS.page.toString();
      newQueryParams["limit"] =
        DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS.limit.toString();

      setIsFilterError(false);
      updateQueryParams({
        ...newQueryParams,
        ...filterRetryCountParams,
      });
      if (
        haveQueryParamsChanged({
          currentSearch: location.search,
          newParams: newQueryParams,
        }) ||
        isFilterError
      ) {
        setIsFiltering(true);
      } else {
        customWarningToast({
          message: NO_NEW_FILTER_MESSAGE,
        });
      }
    };

    const handleRefreshTable = async () => {
      setIsRefreshing(true);
      if (!businessId) {
        console.error("Business id is absent!");
        return;
      }
      if (
        !haveQueryParamsChanged({
          currentSearch: location.search,
          newParams: {},
        })
      ) {
        await handleGetBusinessTransactions(
          {
            businessId,
            ...DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS,
          },
          {
            isRefetching: true,
          }
        );
        setIsRefreshing(false);
      } else {
        setSearchQuery("");
        updateQueryParams({});
      }
    };

    const dropDownRef = useRef<IDropDownRef | null>(null);
    const [dateRange, setDateRange] = useState<IExportDateRangeValues>({
      startDate: null,
      endDate: null,
    });
    const [exportType, setExportType] = useState<TExportType | "">("");

    const handleExportTypeChange = (
      evt: React.ChangeEvent<HTMLInputElement>
    ) => {
      const { value } = evt.target;
      setExportType(value as TExportType);
    };

    const handleDateRangeChange = (name: string, value: Date | null) => {
      if (!value) return;
      setDateRange({
        ...dateRange,
        [name]: value,
      });
    };

    const handleResetExport = () => {
      setDateRange({
        startDate: null,
        endDate: null,
      });
      setExportType("");
    };

    const handleExport = async () => {
      const { startDate, endDate } = dateRange;
      if (!startDate || !endDate || !businessId || !exportType) return;
      const formattedStartDate = formatDateToYYYYMMDD(startDate);
      const formattedEndDate = formatDateToYYYYMMDD(endDate);
      setIsExporting(true);
      const exportPayload: IExportBusinessTransactionsPayload = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        format: exportType,
        businessId,
      };
      try {
        const { data, error, message } =
          await exportBusinessTransactions(exportPayload);
        if (error) {
          customErrorToast({ message });
        } else if (data?.data) {
          const exportData = data?.data;
          if (exportData.length > ZERO) {
            handleDownloadBufferData({
              data: exportData,
              name: `Business_Transactions-${formattedStartDate}-${formattedEndDate}`,
              type: exportType,
            });
            customSuccessToast({ message: capitalizeWords(message) });
            handleResetExport();
            if (dropDownRef.current) {
              dropDownRef.current.handleToggleDropdown();
            }
          } else {
            customInfoToast({
              message: NO_EXPORT_DATA_FOUND,
            });
          }
        } else {
          customErrorToast({
            message: "No data returned",
          });
        }
      } catch (error) {
        console.error("Error: ", error);
      }
      setIsExporting(false);
    };

    const filterOptions: TFilterOption[] = useMemo(() => {
      if (
        isLoadingBusinessAccounts ||
        businessAccountsData.businessAccount.length === ZERO
      ) {
        return DEFAULT_FILTER_OPTIONS;
      }

      if (DEFAULT_FILTER_OPTIONS.length === MAX_TRANSACTION_FILTER_OPTIONS) {
        return DEFAULT_FILTER_OPTIONS;
      }
      const sourceAccountOptions = businessAccountsData.businessAccount.map(
        ({ accountName }) => ({
          label: accountName,
          value: accountName,
        })
      );

      const sourceAccountFilterOption: TFilterOption = {
        type: "select",
        title: "Source Account",
        key: "sourceAccount",
        placeholder: "Select a source account",
        options: sourceAccountOptions,
      };

      DEFAULT_FILTER_OPTIONS.splice(
        SOURCE_ACCOUNT_POSITION,
        START_INDEX,
        sourceAccountFilterOption
      );
      return DEFAULT_FILTER_OPTIONS;
    }, [isLoadingBusinessAccounts, businessAccountsData]);

    useEffect(() => {
      if (!isLoading && filterRef?.current?.isFilterOpen) {
        setIsFiltering(false);
        filterRef?.current?.handleToggleFilter();
      }
    }, [isLoading, filterRef]);

    useEffect(() => {
      if (!isLoading && isRefreshing) {
        handleResetFilter();
        setIsRefreshing(false);
      }
    }, [isLoading]);

    return (
      <Box className="flex px-3 items-center justify-between h-[85px] gap-4 w-[736px]">
        <TableSearchInput
          searchQuery={searchQuery}
          handleSearchQueryChange={handleSearchQueryChange}
        />
        <Filter
          options={filterOptions}
          values={filterValues}
          onChange={handleFilterValueChange}
          onApplyFilter={handleApplyFilter}
          filterBtnCss={tableHeaderCtaCss}
          isLoading={isFiltering}
          ref={filterRef}
          isApplyBtnDisabled={isFilterDisabled}
          handleResetFilter={handleResetFilter}
        />
        <Export
          isLoading={isExporting}
          handleExport={handleExport}
          dateRange={dateRange}
          exportType={exportType}
          handleExportTypeChange={handleExportTypeChange}
          handleDateRangeChange={handleDateRangeChange}
          dropDownRef={dropDownRef}
          availableExportOptions={AVAILABLE_EXPORT_OPTIONS}
          handleResetExport={handleResetExport}
        />
        <Button
          css={tableHeaderCtaCss}
          onClick={!isRefreshing ? handleRefreshTable : undefined}
          disabled={isRefreshing}
        >
          <RefreshIcon
            className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
          />
          <p className="text-sm text-[#374151] font-semibold">Refresh</p>
        </Button>
      </Box>
    );
  }
);
